import React, { useState, useEffect, useRef } from 'react';
import { db, useOutsideClick } from '../../firebase';
import { doc, updateDoc, getDoc, collection, onSnapshot } from 'firebase/firestore';
import { CogIcon } from '@heroicons/react/outline';
import { ROLES } from '../../constants/roles';
import { toast } from 'react-toastify';

function ManageUserRole({ companyId }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersVisible, setUsersVisible] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [saveCall, setSaveCall] = useState(false);

  const usersRef = useRef();
  useOutsideClick(usersRef, () => setUsersVisible(false));

  // Fetch all users
  useEffect(() => {
    if (!companyId) {
      setError('Company ID is required');
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'user_data', companyId, 'users'),
      (snapshot) => {
        setUsers(snapshot.docs);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching users:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  // Handle save changes
  useEffect(() => {
    const saveChanges = async () => {
      if (!selectedUser?.data || !pendingRole || !saveCall) return;

      try {
        const userDoc = doc(db, 'user_data', companyId, 'users', selectedUser.id);

        // First fetch the latest user data
        const latestUserData = await getDoc(userDoc);
        if (!latestUserData.exists()) {
          throw new Error('User no longer exists');
        }

        // Get the current data
        const currentData = latestUserData.data();

        // Ensure pendingRole is valid before proceeding
        if (!pendingRole) {
          throw new Error('Role is undefined or invalid');
        }

        // If the pendingRole is the same as the current role, don't proceed
        if (currentData.role === pendingRole) {
          setSaveCall(false);
          return; // No need to update if the role is unchanged
        }

        // Update the role
        await updateDoc(userDoc, {
          role: pendingRole,
        });

        // Create a new user object with updated data
        const updatedUserData = {
          ...currentData,
          role: pendingRole,
        };

        // Create a new user document snapshot
        const updatedUser = {
          id: selectedUser.id,
          ref: selectedUser.ref,
          data: function () {
            return updatedUserData;
          },
          exists: function () {
            return true;
          },
        };

        setSelectedUser(updatedUser);
        setPendingRole(pendingRole);
        setSaveCall(false);
        toast.success(`Role updated successfully for ${updatedUserData.email}`);
      } catch (error) {
        console.error('Error updating role:', error);
        setError(error.message);
        setSaveCall(false);
        toast.error('Failed to update role: ' + error.message);

        // Reset pendingRole to current role on error
        if (selectedUser?.data()) {
          setPendingRole(selectedUser.data().role);
        }
      }
    };

    saveChanges();
  }, [saveCall, selectedUser, pendingRole, companyId]);

  const handleRoleChange = (userId, newRole) => {
    if (!newRole?.id) return;
    setPendingRole(newRole.id);
  };

  const selectUser = (user) => {
    if (!user?.data) return;
    const userData = user.data();
    if (!userData) return;

    setSelectedUser(user);
    setPendingRole(userData.role || null);
    setUsersVisible(false);
  };

  if (!companyId) {
    return (
      <div className="rounded-lg bg-white drop-shadow-lg p-4 w-[100%] lg:w-[75%] mt-16">
        <div className="text-red-500">Error: Company ID is required</div>
      </div>
    );
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="rounded-lg bg-white drop-shadow-lg p-4 w-[100%] lg:w-[75%] mt-16 flex flex-col justify-between relative">
      <div>
        <div className="mb-5">
          <CogIcon className="w-12 h-12 bg-amber-400 text-white p-2 m-1 rounded-full" />
        </div>
        <p className="text-2xl p-1 m-1">Manage User Roles</p>
        <div className="mt-3">
          <div className="w-[100%] px-2">
            <div ref={usersRef} className="relative z-50">
              <button
                className="w-[100%] border rounded-lg p-1"
                onClick={() => setUsersVisible((prev) => !prev)}
              >
                {selectedUser?.data().email || 'Select User'}
              </button>
              <div className="absolute bg-white w-[100%] max-h-44 overflow-y-auto px-1 drop-shadow-lg rounded-lg rounded-tl-none">
                <div className={usersVisible ? '' : 'hidden'}>
                  {users.map((user) => (
                    <button
                      key={user.id}
                      className="w-full text-left p-2 hover:bg-amber-400 hover:text-white rounded-lg transition-all"
                      onClick={() => selectUser(user)}
                    >
                      {user.data().email}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {selectedUser && (
              <>
                <div className="border rounded-lg p-2 mt-4 flex justify-center gap-2">
                  {ROLES.map((roleOption) => (
                    <button
                      key={roleOption.id}
                      className={`px-4 py-2 rounded-lg transition-all ${
                        pendingRole === roleOption.id
                          ? 'bg-amber-400 text-white'
                          : 'bg-white text-amber-400 border border-amber-400 hover:bg-amber-50'
                      }`}
                      onClick={() => handleRoleChange(selectedUser.id, roleOption)}
                    >
                      {roleOption.label}
                    </button>
                  ))}
                </div>
                <div className="mt-4 mx-1">
                  <button
                    className={`w-[100%] p-2 rounded-lg transition-all ${
                      pendingRole !== selectedUser.data().role
                        ? 'bg-amber-400 text-white hover:shadow-md'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    onClick={() => pendingRole !== selectedUser.data().role && setSaveCall(true)}
                    disabled={pendingRole === selectedUser.data().role}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageUserRole;
